/**
 * Shared-State Analytics Events
 *
 * Reactive listeners that fire analytics by observing shared state stores.
 * These live here rather than in coordinators because they watch stores they
 * don't own — the coordinator architecture reserves inline analytics for
 * lifecycle moments a coordinator directly controls.
 *
 * Events covered:
 *  - User Authenticated           (fires when loginProfileState receives a hostAuthId)
 *  - Edition Set                  (fires when hostState.edition is populated)
 *  - Host Entered Guest Mode      (fires once per session when machineId arrives in guest-mode Electron)
 *  - Broadcast Started            (fires when hostState transitions to 'streaming')
 *  - Broadcast Ended              (fires when hostState transitions to 'stopping')
 *  - Host Added Player            (fires when a player entry appears in playerState)
 *  - Host Removed Player          (fires when a player entry leaves playerState)
 *  - Player Access Rights Updated (fires when a player's permissions change)
 *
 * TODO: When the coordinator path exposes per-player identity on join/leave,
 * the player listeners below can be removed and replaced with coordinator event calls.
 */

import { getStateStore } from '../../stores';
import { loginProfileState, hostState, matchmakerState, machineIdState } from '../../stores/shared';
import { serviceContainer } from '../../../services/ServiceContainer';
import { notifyPlayerInstall } from '../managers/notifications';

export const initLegacyIpcAnalyticsEvents = (): void => {
  const ipcStores = getStateStore('ipc');

  // User Authenticated / Install Email — fires when both hostAuthId and machineId are available.
  // Both listeners call checkAndSendUserAuthenticated so whichever arrives last triggers the send,
  // matching the same guard pattern used by useAppLaunchedAnalytics.
  let lastTrackedAuthId: string | undefined;

  const checkAndSendUserAuthenticated = () => {
    const authId = loginProfileState.currentState.hostAuthId;
    const machineId = machineIdState.currentState;

    if (!authId || authId === lastTrackedAuthId) return;
    if (!machineId) return;

    lastTrackedAuthId = authId;
    serviceContainer.analyticsManager.sendUserAuthenticatedEvent();
    notifyPlayerInstall();
  };

  loginProfileState.addListener(checkAndSendUserAuthenticated);
  machineIdState.addListener(checkAndSendUserAuthenticated);

  // Edition Set — fires when hostState.edition is populated
  hostState.addListener((state) => {
    if (state.edition) {
      serviceContainer.analyticsManager.sendEditionSetEvent(state.edition);
    }
  });

  // Host Entered Guest Mode — fires once per session when machineId arrives (Electron guest mode only)
  if (window.playcast?.isHost && window.playcast?.guestAccount) {
    const SESSION_KEY = 'hasSentHostEnteredGuestMode';
    let sent = false;

    const trySend = (machineId: string) => {
      if (sent || !machineId) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sent = true;
      serviceContainer.analyticsManager.sendHostEnteredGuestModeEvent(machineId);
      sessionStorage.setItem(SESSION_KEY, 'true');
    };

    machineIdState.addListener((machineId) => trySend(machineId));
    trySend(machineIdState.currentState); // fire immediately if already available
  }

  // Broadcast Started / Broadcast Ended — IPC hostState status transitions
  ipcStores.hostState.addListener((state, previousState) => {
    const sessionId = ipcStores.hostPresenceState.currentState.hostPeerId || '';
    const invitationId = ipcStores.invitationStateV2.currentState.id || '';

    if (state.status === 'streaming' && previousState?.status !== 'streaming') {
      serviceContainer.analyticsManager.sendBroadcastStartedEvent({
        broadcastSessionId: sessionId,
        invitationId,
      });
    }

    if (state.status === 'stopping' && previousState?.status !== 'stopping') {
      serviceContainer.analyticsManager.sendBroadcastEndedEvent({
        broadcastSessionId: sessionId,
        invitationId,
      });
    }
  });

  ipcStores.playerState.addListener((state, previousState) => {
    const addedPlayers = Object.keys(state.players).filter(
      (p) => !Object.keys(previousState?.players || {}).includes(p),
    );
    const removedPlayers = Object.keys(previousState?.players || {}).filter(
      (p) => !Object.keys(state.players).includes(p),
    );

    // Prefer the matchmaker invitation ID (populated for all host types including pavilion/anonymous);
    // fall back to invitationStateV2 for standard-edition/lobby hosts that bypass matchmaking.
    const invitationId =
      matchmakerState.currentState.stateDetails?.currentMatchInvitationId ||
      ipcStores.invitationStateV2.currentState.id ||
      '';

    const trackAccessChanged = (playerId: string) => {
      const connectedPersistentId = state.players[playerId].persistentId;
      const statesToCheck = { ...state.players[playerId].access, chat: false };
      const isSpectator = Object.values(statesToCheck).reduce((acc, val) => acc && !val, true);

      serviceContainer.analyticsManager.sendPlayerAccessRightsUpdatedEvent({
        connectedPlayerAuthId: state.players[playerId].playerAuthId,
        connectedPersistentId,
        playerName: state.players[playerId].displayName,
        invitationId,
        access: { ...state.players[playerId].access, spectator: isSpectator },
      });
    };

    addedPlayers.forEach((playerId) => {
      const playerAuthId = previousState?.players[playerId]?.playerAuthId ?? '';

      serviceContainer.analyticsManager.sendHostAddedPlayerEvent({
        connectedPlayerAuthId: playerAuthId,
        connectedPersistentId: state.players[playerId].persistentId,
        playerName: state.players[playerId].displayName,
        invitationId,
      });
    });

    removedPlayers.forEach((playerId) => {
      const playerAuthId = previousState?.players[playerId]?.playerAuthId ?? '';

      serviceContainer.analyticsManager.sendHostRemovedPlayerEvent({
        connectedPlayerAuthId: playerAuthId,
        connectedPersistentId: previousState?.players[playerId]?.persistentId ?? '',
        playerName: previousState?.players[playerId]?.displayName ?? '',
        closedBy: 'host',
        invitationId,
      });
    });

    // Track access changes for existing players
    Object.keys(state.players).forEach((playerId) => {
      const accessChanged = Object.keys(state.players[playerId].access).filter(
        (access) =>
          state.players[playerId].access[access] !== previousState?.players[playerId]?.access[access],
      );
      if (accessChanged.length) {
        trackAccessChanged(playerId);
      }
    });
  });
};
